// Tiny, safe Markdown renderer für Agent-Bios.
// Unterstützt: **fett**, *kursiv*, [text](url), Zeilenumbrüche, Auto-Links http(s)://…
// Kein HTML-Pass-Through — alles wird escaped, dann formatiert.
//
// Bewusst minimal um XSS-Risiko auszuschließen.

import type { ReactNode } from "react";
import { Fragment, createElement } from "react";

const URL_RE = /(https?:\/\/[^\s)]+)/g;
const LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;
const BOLD_RE = /\*\*([^*]+)\*\*/g;
const ITALIC_RE = /(^|[^*])\*([^*]+)\*(?!\*)/g;

type Segment =
  | { t: "text"; v: string }
  | { t: "bold"; v: string }
  | { t: "italic"; v: string }
  | { t: "link"; v: string; href: string };

function isSafeUrl(url: string): boolean {
  const lower = url.toLowerCase().trim();
  return (
    lower.startsWith("http://") ||
    lower.startsWith("https://") ||
    lower.startsWith("mailto:")
  );
}

function parseLine(line: string): Segment[] {
  // Auf einer Linie sukzessive: links, bold, italic, plain.
  // Wir tokenisieren via ersten Match.
  const segments: Segment[] = [];
  let rest = line;
  while (rest.length > 0) {
    const linkMatch = LINK_RE.exec(rest);
    LINK_RE.lastIndex = 0;
    const boldMatch = BOLD_RE.exec(rest);
    BOLD_RE.lastIndex = 0;
    const italicMatch = ITALIC_RE.exec(rest);
    ITALIC_RE.lastIndex = 0;
    const urlMatch = URL_RE.exec(rest);
    URL_RE.lastIndex = 0;

    // Welcher Match kommt zuerst?
    type C = { idx: number; kind: "link" | "bold" | "italic" | "url"; m: RegExpExecArray };
    const cands: C[] = [];
    if (linkMatch) cands.push({ idx: linkMatch.index, kind: "link", m: linkMatch });
    if (boldMatch) cands.push({ idx: boldMatch.index, kind: "bold", m: boldMatch });
    if (italicMatch)
      cands.push({ idx: italicMatch.index + italicMatch[1].length, kind: "italic", m: italicMatch });
    if (urlMatch) cands.push({ idx: urlMatch.index, kind: "url", m: urlMatch });

    if (cands.length === 0) {
      segments.push({ t: "text", v: rest });
      break;
    }

    cands.sort((a, b) => a.idx - b.idx);
    const c = cands[0];
    if (c.idx > 0) {
      segments.push({ t: "text", v: rest.slice(0, c.idx) });
    }

    if (c.kind === "link") {
      const url = c.m[2];
      const txt = c.m[1];
      if (isSafeUrl(url)) {
        segments.push({ t: "link", v: txt, href: url });
      } else {
        segments.push({ t: "text", v: c.m[0] });
      }
      rest = rest.slice(c.idx + c.m[0].length);
    } else if (c.kind === "bold") {
      segments.push({ t: "bold", v: c.m[1] });
      rest = rest.slice(c.idx + c.m[0].length);
    } else if (c.kind === "italic") {
      segments.push({ t: "italic", v: c.m[2] });
      rest = rest.slice(c.idx + c.m[1].length + c.m[2].length + 2);
    } else {
      // bare URL
      const url = c.m[1];
      segments.push({ t: "link", v: url, href: url });
      rest = rest.slice(c.idx + url.length);
    }
  }
  return segments;
}

function segmentsToNode(segs: Segment[]): ReactNode {
  return segs.map((s, i) => {
    if (s.t === "bold") return createElement("strong", { key: i }, s.v);
    if (s.t === "italic") return createElement("em", { key: i }, s.v);
    if (s.t === "link")
      return createElement(
        "a",
        {
          key: i,
          href: s.href,
          target: "_blank",
          rel: "noopener noreferrer",
          className: "underline hover:opacity-80",
        },
        s.v,
      );
    return createElement(Fragment, { key: i }, s.v);
  });
}

export function renderMarkdown(text: string | null | undefined): ReactNode {
  if (!text) return null;
  // Paragraphen via doppeltem Newline trennen
  const paragraphs = text.replace(/\r\n/g, "\n").split(/\n{2,}/);
  return paragraphs.map((p, pi) => {
    // Innerhalb eines Paragraphen: einfache Newlines → <br>
    const lines = p.split("\n");
    return createElement(
      "p",
      { key: pi, className: pi > 0 ? "mt-3" : undefined },
      lines.flatMap((line, li) => {
        const segs = parseLine(line);
        const rendered = segmentsToNode(segs);
        return li === 0
          ? rendered
          : [createElement("br", { key: `br${li}` }), ...((rendered as ReactNode[]) ?? [])];
      }),
    );
  });
}
